/**
 * Generic Highcharts heatmap options builder.
 *
 * Callers are responsible for transforming their data into the required input format.
 *
 * @param {object}    params
 * @param {string[]}  params.xCategories           - Labels for x-axis columns.
 * @param {string[]}  params.yCategories           - Labels for y-axis rows.
 * @param {object[]}  params.seriesData            - Highcharts point objects: { x, y, value, color?, custom? }.
 * @param {number}   [params.cellSize=22]          - Width/height of each cell in px.
 * @param {number}   [params.cellGap=2]            - Gap between cells in px (added to cellSize for stride).
 * @param {number}   [params.yAxisWidth=150]       - Left margin reserved for y-axis labels in px.
 * @param {number}   [params.headerHeight=72]      - Top margin reserved for x-axis labels in px.
 * @param {number}   [params.marginRight=10]
 * @param {number}   [params.marginBottom=10]
 * @param {string}   [params.nullColor="#f0f0f0"]  - Fill colour for null/missing cells.
 * @param {number}   [params.cellBorderWidth=2]
 * @param {string}   [params.cellBorderColor="#fff"]
 * @param {boolean}  [params.xAxisOpposite=true]   - Render x-axis at the top.
 * @param {number}   [params.xAxisRotation=-45]    - Label rotation in degrees.
 * @param {string}   [params.xAxisLabelAlign="left"]
 * @param {string}   [params.xAxisFontSize="10px"]
 * @param {string}   [params.xAxisColor="#595959"]
 * @param {string}   [params.yAxisLabelAlign="right"]
 * @param {string}   [params.yAxisFontSize="12px"]
 * @param {string}   [params.yAxisColor="#434343"]
 * @param {boolean}  [params.yAxisReversed=false]
 * @param {number}   [params.colorAxisMin=0]
 * @param {number}   [params.colorAxisMax=1]
 * @param {boolean}  [params.colorAxisVisible=false]
 * @param {Function|null} [params.tooltipFormatter=null] - Highcharts `this`-context formatter.
 *                                                         Pass null to disable the tooltip entirely.
 * @param {boolean}  [params.animation=false]
 * @param {string}   [params.fontFamily="inherit"]
 * @param {boolean}  [params.legendEnabled=false]
 * @param {number}   [params.footerHeight=0]            - Extra bottom space in px (e.g. for bottom x-axis
 *                                                        labels). Added to both chartHeight and marginBottom.
 * @param {string}   [params.backgroundColor="#ffffff"] - Chart background colour. Pass "transparent" when
 *                                                        the host container provides its own background.
 * @returns {object} Highcharts options object ready to pass to <HighchartsReact />.
 */
export const getHeatmapOptions = ({
    xCategories,
    yCategories,
    seriesData,
    cellSize = 22,
    cellGap = 2,
    yAxisWidth = 150,
    headerHeight = 72,
    marginRight = 10,
    marginBottom = 10,
    nullColor = "#f0f0f0",
    cellBorderWidth = 2,
    cellBorderColor = "#fff",
    xAxisOpposite = true,
    xAxisRotation = -45,
    xAxisLabelAlign = "left",
    xAxisFontSize = "10px",
    xAxisColor = "#595959",
    yAxisLabelAlign = "right",
    yAxisFontSize = "12px",
    yAxisColor = "#434343",
    yAxisReversed = false,
    colorAxisMin = 0,
    colorAxisMax = 1,
    colorAxisVisible = false,
    tooltipFormatter = null,
    animation = false,
    fontFamily = "inherit",
    legendEnabled = false,
    footerHeight = 0,
    backgroundColor = "#ffffff",
}) => {
    const chartWidth = yAxisWidth + xCategories.length * (cellSize + cellGap) + marginRight;
    const chartHeight = headerHeight + yCategories.length * (cellSize + cellGap) + footerHeight + marginBottom;

    return {
        chart: {
            type: "heatmap",
            width: chartWidth,
            height: chartHeight,
            marginTop: headerHeight,
            marginRight,
            marginBottom: marginBottom + footerHeight,
            marginLeft: yAxisWidth,
            animation,
            backgroundColor,
            style: { fontFamily },
        },
        title: { text: undefined },
        credits: { enabled: false },
        legend: { enabled: legendEnabled },
        xAxis: {
            categories: xCategories,
            opposite: xAxisOpposite,
            labels: {
                rotation: xAxisRotation,
                align: xAxisLabelAlign,
                style: { fontSize: xAxisFontSize, color: xAxisColor },
            },
            lineWidth: 0,
            tickLength: 0,
        },
        yAxis: {
            categories: yCategories,
            title: { text: null },
            reversed: yAxisReversed,
            labels: {
                align: yAxisLabelAlign,
                style: { fontSize: yAxisFontSize, color: yAxisColor },
            },
            lineWidth: 0,
            tickLength: 0,
            gridLineWidth: 0,
        },
        colorAxis: { min: colorAxisMin, max: colorAxisMax, visible: colorAxisVisible },
        tooltip: tooltipFormatter
            ? { useHTML: true, formatter: tooltipFormatter }
            : { enabled: false },
        series: [
            {
                type: "heatmap",
                data: seriesData,
                nullColor,
                borderWidth: cellBorderWidth,
                borderColor: cellBorderColor,
            },
        ],
    };
};
